// PROV-01 · R92 · ENTRADA PRINCIPAL: Persona registrada -> Accesos App -> cuentas autorizadas.
// Misma implementación que la pestaña «Acceso App»: reutiliza sus modales y sus endpoints de
// mutación. La Persona queda FIJA y visible; aquí no se modifica el expediente ni sus endpoints.
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { notifyOk, notifyError, notifyWarn } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  OUTLINE, PRIMARIO, AZUL, rolLabel, buscarCuentas, grantsPorPersona, errorDe, usePaginado,
  CargarMas, GrantEstadoBadges, ConfirmAccesoApp, CuentaAppModal, GrantModal, ResetPasswordAppModal,
} from "@/components/admin/accesoAppShared";
import { Smartphone, KeyRound, Plus } from "lucide-react";

export default function PersonaAccesosApp({ personaId }) {
  const [persona, setPersona] = useState(null);
  const [asignable, setAsignable] = useState(false);
  const [email, setEmail] = useState("");
  const [buscada, setBuscada] = useState(null);   // null | { encontrada:false } | cuenta
  const [buscando, setBuscando] = useState(false);
  const [openCuenta, setOpenCuenta] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [grantCtx, setGrantCtx] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const pag = usePaginado((extra) => grantsPorPersona({ persona_id: personaId, ...extra }));
  const token = useRef(0);

  const cargar = async () => {
    const d = await pag.pedir({});
    if (d) { setPersona(d.persona || null); setAsignable(!!d.asignable); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [personaId]);

  const buscarCuenta = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    const mio = ++token.current;
    setBuscando(true);
    try {
      const r = await buscarCuentas({ email_exacto: e, limite: 1 });
      if (mio !== token.current) return;
      const c = (r.data.items || [])[0] || null;
      setBuscada(c || { encontrada: false, email: e });
    } catch (err) {
      if (mio === token.current) { setBuscada(null); notifyError(errorDe(err)); }
    } finally { if (mio === token.current) setBuscando(false); }
  };

  const cuentaEncontrada = buscada && buscada.user_id ? buscada : null;

  const revocar = async (gid) => {
    try {
      await api.post(`/app-admin/grants/${gid}/revocar`);
      notifyOk("Acceso revocado. La cuenta conserva sus otros accesos.");
      await cargar();
      return true;
    } catch (e) { notifyError(errorDe(e)); return false; }
  };

  const conf = confirmar || {};

  return (
    <div className="tema-noche-auto space-y-5" data-testid={`persona-accesos-app-${personaId}`}>
      {/* La Persona permanece fija y visible: toda acción se lee contra ESTE expediente. */}
      <div className="rounded-2xl border border-gray-100 bg-slate-50 p-4">
        <p className="text-xs uppercase tracking-wider text-slate-400">Persona protegida</p>
        <p className="font-medium text-[#012472] break-words" data-testid="persona-accesos-identidad">
          {persona?.nombre_completo || "—"}
        </p>
        <p className="text-xs text-slate-500 break-words">
          {persona?.sitio_nombre || "Sin sitio"}
          {persona?.organization_nombre ? ` · ${persona.organization_nombre}` : ""}
          {persona?.habitacion ? ` · ${persona.habitacion}` : ""}
          {persona?.identificador_corto ? ` · ref ${persona.identificador_corto}` : ""}
          {persona?.estado ? ` · ${persona.estado}` : ""}
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Una Persona protegida no es una cuenta de login. El parentesco, el pago y ser contacto de
          emergencia NO conceden acceso: sólo lo concede un acceso explícito a una cuenta App.
        </p>
        {!asignable && (
          <p className="text-xs text-amber-700 mt-2" data-testid="persona-accesos-no-asignable">
            Esta persona (o su sitio u organización) no está operativa: el histórico se consulta,
            pero no se pueden otorgar accesos nuevos.
          </p>
        )}
      </div>

      {asignable && (
        <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
          <p className="text-sm font-medium text-[#012472]">Autorizar una cuenta App</p>
          <p className="text-xs text-slate-500">
            Busca la cuenta por su correo exacto. Si ya existe se reutiliza tal cual (no se cambia
            su nombre, su rol ni su contraseña). Crear la cuenta y otorgar el acceso son dos
            operaciones separadas.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={email} type="email" className="rounded-xl" placeholder="correo@ejemplo.com"
              data-testid="persona-accesos-email-input"
              onChange={(e) => { setEmail(e.target.value); setBuscada(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") buscarCuenta(); }} />
            <Button variant="outline" className={OUTLINE} disabled={buscando || !email.trim()}
              data-testid="persona-accesos-buscar-cuenta" onClick={buscarCuenta}>
              {buscando ? "Buscando…" : "Buscar cuenta"}
            </Button>
          </div>

          {buscada && !cuentaEncontrada && (
            <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-2"
              data-testid="persona-accesos-sin-cuenta">
              <p className="text-sm text-slate-600 break-all">
                No existe una cuenta App con <span className="font-medium">{buscada.email}</span>.
              </p>
              <Button size="sm" className={PRIMARIO} data-testid="persona-accesos-crear-cuenta"
                onClick={() => setOpenCuenta(true)}>
                <Plus strokeWidth={1.75} className="h-4 w-4 mr-1.5" /> Nueva cuenta App
              </Button>
            </div>
          )}

          {cuentaEncontrada && (
            <div className="rounded-xl border border-gray-100 bg-slate-50 p-3 space-y-2"
              data-testid="persona-accesos-cuenta-encontrada">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#012472] break-all">{cuentaEncontrada.email}</p>
                  <p className="text-xs text-slate-500">
                    {cuentaEncontrada.nombre} · {rolLabel(cuentaEncontrada.role)} ·{" "}
                    {cuentaEncontrada.activa ? "Cuenta activa" : "Cuenta inactiva"} ·{" "}
                    {cuentaEncontrada.grants_activos} acceso
                    {cuentaEncontrada.grants_activos === 1 ? "" : "s"} vigente
                    {cuentaEncontrada.grants_activos === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className={OUTLINE}
                    data-testid="persona-accesos-reset-password" onClick={() => setOpenReset(true)}>
                    <KeyRound strokeWidth={1.5} className="h-4 w-4 mr-1.5" /> Restablecer contraseña
                  </Button>
                  <Button size="sm" className={AZUL} data-testid="persona-accesos-otorgar"
                    onClick={() => setGrantCtx({ cuenta: cuentaEncontrada, persona, grant: null })}>
                    Otorgar acceso a esta persona
                  </Button>
                </div>
              </div>
              {!cuentaEncontrada.activa && (
                <p className="text-xs text-amber-700">
                  La cuenta está inactiva: no podrá iniciar sesión en la App hasta que se active.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        <p className="text-sm font-medium text-[#012472]">Accesos de esta persona</p>
        {pag.items.length === 0 && !pag.cargando && (
          <p className="text-sm text-slate-400" data-testid="persona-accesos-sin-grants">
            Ninguna cuenta App tiene acceso a esta persona.
          </p>
        )}
        {pag.items.map((g) => (
          <div key={g.grant_id} data-testid={`persona-accesos-grant-${g.grant_id}`}
            className="rounded-xl border border-gray-100 bg-white p-4 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-[#012472] break-all flex items-center gap-1.5">
                  <Smartphone strokeWidth={1.5} className="h-4 w-4 shrink-0" />
                  {g.cuenta?.email || g.user_id}
                </p>
                <p className="text-xs text-slate-500 break-words">
                  {g.cuenta?.nombre || "(sin nombre)"} · {rolLabel(g.cuenta?.role)} ·{" "}
                  {g.cuenta?.activa ? "Cuenta activa" : "Cuenta inactiva"} ·{" "}
                  {g.tipo === "propio" ? "Acceso propio" : "Acceso familiar"}
                  {g.relacion ? ` · ${g.relacion}` : ""}
                </p>
              </div>
              <GrantEstadoBadges grant={g}
                testId={`persona-accesos-grant-estado-${g.grant_id}`} />
            </div>
            <p className="text-xs text-slate-400">
              Permisos: {(g.permisos || []).join(", ") || "—"}
              {g.vigencia_hasta ? ` · Hasta ${g.vigencia_hasta.slice(0, 10)}` : " · Sin fecha límite"}
            </p>
            {g.estado === "activo" && (
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" className={OUTLINE}
                  data-testid={`persona-accesos-editar-${g.grant_id}`}
                  onClick={() => setGrantCtx({ cuenta: g.cuenta, persona, grant: g })}>Editar</Button>
                <Button variant="ghost" size="sm"
                  className="rounded-xl text-rose-700 hover:bg-rose-50 hover:text-rose-800 focus-visible:ring-2 focus-visible:ring-rose-300"
                  data-testid={`persona-accesos-revocar-${g.grant_id}`}
                  onClick={() => setConfirmar({ tipo: "revocar", grant: g })}>Revocar acceso</Button>
              </div>
            )}
          </div>
        ))}
        <CargarMas hayMas={pag.hayMas} cargando={pag.cargando} testId="persona-accesos-cargar-mas"
          onClick={() => pag.pedir({}, true)} />
      </div>

      <CuentaAppModal open={openCuenta} onOpenChange={setOpenCuenta} emailSugerido={email.trim().toLowerCase()}
        onCreada={async (c) => {
          setBuscada(c);
          setEmail(c.email);
          notifyWarn("Cuenta creada. Ahora otorga el acceso a esta persona (operación aparte).");
        }} />

      <GrantModal open={!!grantCtx} onOpenChange={(v) => { if (!v) setGrantCtx(null); }}
        cuenta={grantCtx?.cuenta} persona={grantCtx?.persona} grant={grantCtx?.grant}
        onGuardado={cargar} />

      {cuentaEncontrada && (
        <ResetPasswordAppModal open={openReset} onOpenChange={setOpenReset} cuenta={cuentaEncontrada}
          onHecho={cargar} />
      )}

      <ConfirmAccesoApp
        open={conf.tipo === "revocar"} onOpenChange={(v) => { if (!v) setConfirmar(null); }}
        titulo="Revocar este acceso" testId="persona-accesos-confirm-revocar" destructivo
        confirmar="Revocar acceso"
        consecuencia="Retira UN acceso, no la cuenta. La sesión activa de esa cuenta se cierra y deberá entrar de nuevo."
        lineas={[
          `Cuenta App: ${conf.grant?.cuenta?.email || ""}`,
          `Persona afectada: ${persona?.nombre_completo || ""}`,
          "El monitoreo de la persona no cambia; los otros accesos de esa cuenta se conservan.",
        ]}
        onConfirm={() => revocar(conf.grant.grant_id)} />
    </div>
  );
}
